// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file AssessmentBackground — persistent ambient backdrop for the assessment flow
 * @version 0.2.0
 */

import React from 'react';
import styles from './AssessmentBackground.module.css';

/*
 * AssessmentBackground — persistent ambient backdrop for the assessment-taking flow.
 *
 *   <AssessmentBackground />
 *
 * Renders a dark-navy base surface, a soft radial glow centered above the
 * viewport, and a trefoil of three overlapping outline circles to the right
 * of center carrying short bright-teal arc segments + teal dot accents that
 * orbit at mechanical-watch pace. On viewports ≥ 1920px a secondary pair of
 * outline circles extends the composition rightward + downward.
 *
 * Positioning model: the glow centers on the viewport horizontally; the
 * trefoil's left edge sits at `calc(50% + 144px)` so it stays consistently
 * offset from viewport center across breakpoints. The decoration recedes via
 * opacity at narrower viewports — by intent, the trefoil should fade out of
 * the way, not compete for attention with foreground content.
 *
 * Mount once at the parent level above any screen-swap boundary so the orbit
 * phases persist across screen transitions rather than resetting on each step.
 */

// ── glow geometry ────────────────────────────────────────────────────────
const GLOW_SIZE = 1064;
const GLOW_TOP = -525;
const GLOW_INNER_OPACITY = 0.85;

// ── trefoil reference frame ──────────────────────────────────────────────
// SVG positioned at `left: calc(50% + 44px); top: -250px` (see module.css).
// ViewBox origin offset by (-100, -250) so element coordinates can be
// written directly in the design's natural coord system (graphic 1's left
// edge corresponds to viewBox x=0, which lands at viewport x=center+144).
const VB_X = -100;
const VB_Y = -250;
const VB_WIDTH = 1600;
const VB_HEIGHT = 1800;

// ── primary trefoil — always visible ─────────────────────────────────────

const CIRCLES = [
  { key: '1', cx: 236,   cy: 127,   r: 297.55 },
  { key: '3', cx: 437,   cy: 415,   r: 195    },
  { key: '2', cx: 592.5, cy: 128.5, r: 266.5  },
] as const;

const ARCS = [
  { key: '1', cx: 236,   cy: 127,   r: 297.55, startAngle: -34, endAngle: -21, orbitSeconds: 252, orbitDirection: 'cw'  },
  { key: '3', cx: 437,   cy: 415,   r: 195,    startAngle:  72, endAngle:  90, orbitSeconds: 180, orbitDirection: 'ccw' },
  { key: '2', cx: 592.5, cy: 128.5, r: 266.5,  startAngle:   0, endAngle:  90, orbitSeconds: 234, orbitDirection: 'ccw' },
] as const;

// Dot positions are snapped onto each host circle's outline. The Figma
// source's authored bbox centers were lifted from a smaller earlier circle
// set and didn't reach the (larger) current outlines, so dots floated
// inside the circles. Angles are preserved from the source; only the
// radial distance is set to the circle's r.
const DOTS = [
  // angle ~-178° on circle 1 (r=297.55) — left edge of circle, just below center
  { key: '1', cx: -61.4, cy: 116.3, r: 12.65, orbitCx: 236,   orbitCy: 127,   orbitSeconds: 102, orbitDirection: 'ccw' },
  // angle ~-129.6° on circle 3 (r=195) — upper-left of circle 3
  { key: '3', cx: 312.7, cy: 264.7, r:  8,    orbitCx: 437,   orbitCy: 415,   orbitSeconds:  66, orbitDirection: 'cw'  },
  // angle ~-154.8° on circle 2 (r=266.5) — upper-left of circle 2
  { key: '2', cx: 351.2, cy:  15.3, r:  8,    orbitCx: 592.5, orbitCy: 128.5, orbitSeconds:  84, orbitDirection: 'cw'  },
] as const;

// ── secondary decoration — wide viewports only (≥ 1920px) ────────────────

const CIRCLES_WIDE = [
  { key: '5', cx:  348, cy: 1125, r: 332.3 },
  { key: '6', cx: 1129, cy:   93, r: 275.7 },
] as const;

const ARCS_WIDE = [
  { key: '5', cx:  348, cy: 1125, r: 332.3, startAngle: 116, endAngle: 136, orbitSeconds: 198, orbitDirection: 'cw'  },
  { key: '6', cx: 1129, cy:   93, r: 275.7, startAngle: 117, endAngle: 136, orbitSeconds: 162, orbitDirection: 'ccw' },
] as const;

// Secondary dots — also snapped onto their host outlines (same fix as DOTS).
const DOTS_WIDE = [
  // angle ~-22.4° on circle 5 (r=332.3) — right-of-center, slightly above
  { key: '5', cx:  655.2, cy:  998.4, r: 14.15, orbitCx:  348, orbitCy: 1125, orbitSeconds: 144, orbitDirection: 'ccw' },
  // angle ~-84.6° on circle 6 (r=275.7) — nearly straight up from center (off-screen at start, swings in)
  { key: '6', cx: 1155.1, cy: -181.5, r: 11.3,  orbitCx: 1129, orbitCy:   93, orbitSeconds:  78, orbitDirection: 'cw'  },
] as const;

// Convert (cx, cy, r, angleDeg) to a point — angle 0 = +X (right), 90 = +Y (down) per SVG convention.
function polarPoint(cx: number, cy: number, r: number, angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

// Build an SVG arc path between two angles on a circle.
function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const [x1, y1] = polarPoint(cx, cy, r, startDeg);
  const [x2, y2] = polarPoint(cx, cy, r, endDeg);
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  const sweep = endDeg > startDeg ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} ${sweep} ${x2} ${y2}`;
}

type Circle = { key: string; cx: number; cy: number; r: number };
type Arc = Circle & { startAngle: number; endAngle: number; orbitSeconds: number; orbitDirection: 'cw' | 'ccw' };
type Dot = { key: string; cx: number; cy: number; r: number; orbitCx: number; orbitCy: number; orbitSeconds: number; orbitDirection: 'cw' | 'ccw' };

function renderCircles(circles: readonly Circle[], keyPrefix: string) {
  return circles.map((c) => (
    <circle
      key={`${keyPrefix}-${c.key}`}
      className={styles.circle}
      cx={c.cx} cy={c.cy} r={c.r}
      fill="none"
    />
  ));
}

function renderArcs(arcs: readonly Arc[], keyPrefix: string) {
  return arcs.map((a) => (
    <g
      key={`${keyPrefix}-${a.key}`}
      className={a.orbitDirection === 'cw' ? styles.orbitCw : styles.orbitCcw}
      style={{
        transformOrigin: `${a.cx}px ${a.cy}px`,
        animationDuration: `${a.orbitSeconds}s`,
      }}
    >
      <path
        className={styles.arc}
        d={arcPath(a.cx, a.cy, a.r, a.startAngle, a.endAngle)}
        fill="none"
        strokeLinecap="round"
      />
    </g>
  ));
}

function renderDots(dots: readonly Dot[], keyPrefix: string) {
  return dots.map((d) => (
    <g
      key={`${keyPrefix}-${d.key}`}
      className={d.orbitDirection === 'cw' ? styles.orbitCw : styles.orbitCcw}
      style={{
        transformOrigin: `${d.orbitCx}px ${d.orbitCy}px`,
        animationDuration: `${d.orbitSeconds}s`,
      }}
    >
      <circle
        className={styles.dot}
        cx={d.cx} cy={d.cy} r={d.r}
      />
    </g>
  ));
}

export interface AssessmentBackgroundProps {
  /** Forwarded to the root `<div>`. Use for layout positioning when the
   *  consumer needs to override the default `inset: 0` absolute fill. */
  className?: string;
  /** Default `true`. When `false`, the orbiting arc + dot animations freeze
   *  at their authored starting position — used by surfaces where motion
   *  competes with foreground content (e.g., in-flow question screens). */
  animated?: boolean;
  /**
   * Colour treatment. `dark` (default) is the navy base with white-hairline
   * rings + deep-teal accents; `light` is the light-teal base with grey-120
   * rings + bright-teal (#28BABA) accents. Per-instance variant.
   */
  theme?: 'light' | 'dark';
}

export const AssessmentBackground: React.FC<AssessmentBackgroundProps> = ({ className, animated = true, theme = 'dark' }) => {
  const isLight = theme === 'light';
  const rootClass = [styles.root, isLight ? styles.light : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} aria-hidden="true" data-animated={animated ? 'true' : 'false'}>
      {/* Glow — soft radial gradient centered horizontally on the viewport,
          bleeding in from above the top edge. */}
      <div
        className={styles.glow}
        style={{
          top: GLOW_TOP,
          width: GLOW_SIZE,
          height: GLOW_SIZE,
        }}
      >
        <svg
          width={GLOW_SIZE}
          height={GLOW_SIZE}
          viewBox={`0 0 ${GLOW_SIZE} ${GLOW_SIZE}`}
        >
          <defs>
            <radialGradient id="hd-ab-glow" cx="50%" cy="50%" r={isLight ? '70.71%' : '50%'}>
              <stop offset="0%"                     className={styles.glowStopInner} stopOpacity={isLight ? 0.2 : GLOW_INNER_OPACITY} />
              <stop offset={isLight ? '70%' : '100%'} className={styles.glowStopOuter} stopOpacity={0} />
            </radialGradient>
          </defs>
          <rect width={GLOW_SIZE} height={GLOW_SIZE} fill="url(#hd-ab-glow)" />
        </svg>
      </div>

      {/* Trefoil — anchored to the right of viewport center. */}
      <svg
        className={styles.trefoilSvg}
        viewBox={`${VB_X} ${VB_Y} ${VB_WIDTH} ${VB_HEIGHT}`}
        width={VB_WIDTH}
        height={VB_HEIGHT}
      >
        <g className={styles.trefoil}>
          {renderCircles(CIRCLES, 'c')}
          {renderArcs(ARCS, 'a')}
          {renderDots(DOTS, 'd')}
        </g>

        {/* Secondary decoration — CSS hides this group below 1920px. */}
        <g className={styles.trefoilWide}>
          {renderCircles(CIRCLES_WIDE, 'cw')}
          {renderArcs(ARCS_WIDE, 'aw')}
          {renderDots(DOTS_WIDE, 'dw')}
        </g>
      </svg>
    </div>
  );
};

AssessmentBackground.displayName = 'AssessmentBackground';
